/**
 * The one tie on this site that is not made of money.
 *
 * Everything else that connects a lobbyist to a member here is a cheque. This
 * is a career: on a quarterly LD-2, under penalty of perjury, a registered
 * lobbyist must declare the government post they held before they were a
 * lobbyist, and where that wording names a member of Congress two sworn
 * filings point at the same two people. The second is the money.
 *
 * Which is exactly why the arrangement of it has to be careful. Both facts
 * are filed and neither is in doubt; every wrong thing this section could say
 * comes from putting them next to each other:
 *
 * 1. **A post is not an identity.** `position` is the shortest of the filed
 *    wordings, and on nineteen rows of the corpus a filer once wrote the
 *    member's name into that field with no job title at all. Unlabelled, a
 *    row reading "Omar Franco / Rep. Mario Diaz-Balart" says Omar Franco is
 *    the congressman. So the wording never appears without "Declared the post
 *    as" in front of it, and where the filing gave no title the row says so.
 *    This is the `WhoTheyAre` defect with the sides swapped — two sitting
 *    members appeared there under a heading reading "The people" and looked
 *    like staff at a lobbying firm until a label was put in front of them.
 * 2. **The member did not do this.** They filed neither form. A job in their
 *    office is the lobbyist's own account of the lobbyist's own career, the
 *    money is that same person's account of their own giving, and nothing
 *    joins the two. The paragraph saying so is not decoration; it is the
 *    difference between a disclosure and an accusation.
 * 3. **Nor did the money buy anything.** No filing reports money given for
 *    anything, so there is no figure here for money given because of the
 *    door. The contribution may also predate the post, which the obvious
 *    reading of a job beside a figure gets backwards.
 *
 * Most of Congress gets nothing here at all. 534 members have a declaration
 * pointing at them; for everybody else a heading over a blank space would
 * read as a finding, so the section does not render.
 */

import { Link } from "react-router-dom";
import { type DoorPerson, type MemberDoor, api } from "../api";
import { useAsync } from "../useAsync";
import { lobbyistPath } from "./lobbyist";
import { namesNoPost } from "./door";

/** Exact dollars. These are small figures and the point is that they are the
 *  filed ones, so they are not rounded into a shorthand. */
function money(value: number): string {
  return `$${Math.round(value).toLocaleString()}`;
}

/**
 * Fetched on its own, so a page holding it need know nothing but the id.
 *
 * Nothing at all while it loads or if it fails, as with the other panels on
 * this page: the record is the page, and a failure here should cost the
 * section rather than put an error box in front of the voting record.
 */
export function MemberRevolvingDoor({ bioguide }: { bioguide: string }) {
  const found = useAsync(() => api.memberRevolvingDoor(bioguide), [bioguide]);
  if (!found.data) return null;
  return <RevolvingDoor door={found.data} />;
}

export function RevolvingDoor({ door }: { door: MemberDoor }) {
  // Nothing, rather than a heading saying nothing was found. Absence here is
  // the ordinary state of most of Congress and is not a finding about anyone.
  if (door.people.length === 0) return null;
  const name = door.full_name ?? "This member";
  const shown = door.people.length;

  return (
    <section className="mt-12">
      <h2 className="font-display text-xl font-semibold">Lobbyists who declared working for them</h2>
      <p className="mt-1.5 max-w-3xl text-muted">
        {door.total === 1
          ? "One registered lobbyist has declared"
          : `${door.total.toLocaleString()} registered lobbyists have declared`}{" "}
        a government post naming {name} — the job they held before they were a lobbyist, sworn to on
        a quarterly lobbying report under penalty of perjury. Where the same person also certified
        political money on a separate semi-annual report, what they disclosed giving {name} is
        beside it.
      </p>
      {/* The paragraph the section exists to carry. Two sworn filings about
          one pair of people is a fact; everything a reader is likely to
          assume from the arrangement of them is not in either form. */}
      <p className="mt-2 max-w-3xl text-muted">
        They are two separate disclosures that happen to name the same two people. {name} filed
        neither of them, and neither describes anything {name} did: the post is one person's
        account of their own career, the money is that same person's account of their own giving,
        and no filing connects them.
      </p>

      <ol className="mt-4 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card">
        {door.people.map((person) => (
          <li key={`${person.name}-${person.lobbyist_id ?? person.registrant_id}`}>
            <Row person={person} />
          </li>
        ))}
      </ol>

      <div className="mt-3 max-w-3xl space-y-2 text-sm text-faint">
        {door.total > shown && (
          <p>
            The {shown.toLocaleString()} largest of {door.total.toLocaleString()}.{" "}
            {(door.total - shown).toLocaleString()} more not shown.
          </p>
        )}
        <p>
          A declaration counted here is a distinct wording filed by a distinct registrant rather
          than a filing. The same declaration is refiled every quarter it stays true, so counting
          filings would report one standing job as dozens.
        </p>
        {/* Both directions of the thing a figure beside a job invites: that
            the money followed the job, and that either was for the other. */}
        <p>
          A figure is everything that person certified giving {name} anywhere in this record. It may
          predate the post they declared, and no filing reports money given for anything — so there
          is no figure here for money given because of any of this, and there cannot be one.
        </p>
        <p>
          A row is one person and one member, keyed on the name: a contribution names whoever wrote
          it in free text and carries no identifier, so two lobbyists who share a name would appear
          here as one.
        </p>
      </div>
    </section>
  );
}

/**
 * One person.
 *
 * The order down the row is the order the facts can safely be read in: who
 * they are, what they swore they were, who filed it, and only then the money
 * — which is the half that belongs to a different form.
 */
function Row({ person }: { person: DoorPerson }) {
  const bare = namesNoPost(person.position, person.full_name);
  return (
    <div className="flex flex-wrap items-start gap-x-6 gap-y-2 px-4 py-3">
      <div className="min-w-64 flex-1">
        {/* Their own page, keyed on the registrant that filed the wording
            below and their name — the pair, because no federal form
            identifies a lobbyist across a change of employer. Every one of
            the 2,453 rows this section is built from resolves, since the
            declaration it came off is itself half of the key. */}
        {person.registrant_id !== null ? (
          <Link
            to={lobbyistPath(person.registrant_id, person.name)}
            className="font-medium text-brand hover:underline"
          >
            {person.name}
          </Link>
        ) : (
          <p className="font-medium">{person.name}</p>
        )}
        {/* Never the words on their own. Under a name, "Rep. Mario
            Diaz-Balart" with nothing in front of it reads as who this person
            is rather than as the post they filed. */}
        <p className="mt-0.5 text-sm text-muted">
          <span className="text-faint">Declared the post as </span>
          <span className="italic">“{person.position}”</span>
        </p>
        {bare && (
          <p className="mt-0.5 text-xs text-faint">
            That is the whole of the post field on the filing: it named the office and gave no job
            title.
          </p>
        )}
        <p className="mt-1 text-xs text-faint">
          Filed by{" "}
          {person.registrant_id !== null ? (
            <Link to={`/money/${person.registrant_id}`} className="font-medium hover:text-brand">
              {person.registrant}
            </Link>
          ) : (
            <span className="font-medium">{person.registrant}</span>
          )}
          {person.firms > 1 && <> and {(person.firms - 1).toLocaleString()} other</>}
          {person.firms > 2 && <>s</>} ·{" "}
          {person.declarations === 1 ? "1 declaration" : `${person.declarations} declarations`}
          {person.first_year !== null && (
            <>
              {" · "}
              {person.first_year === person.last_year
                ? person.first_year
                : `${person.first_year}–${person.last_year}`}
            </>
          )}
        </p>
      </div>
      <div className="w-44 text-right">
        {person.to_them > 0 ? (
          <>
            <p className="tnum font-medium">{money(person.to_them)}</p>
            <p className="text-xs text-faint">
              {person.gifts_to_them === 1
                ? "1 disclosed contribution"
                : `${person.gifts_to_them.toLocaleString()} disclosed contributions`}
            </p>
          </>
        ) : (
          // Not a zero in a money column. There is no contribution on file,
          // which is most of these rows, and a figure of nothing reads as a
          // measurement rather than as an absence.
          <p className="text-xs text-faint">No contribution to them on file</p>
        )}
      </div>
    </div>
  );
}
